import * as React from "react";
import { Document, Page } from "react-pdf";
import type { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
import "@/components/feature/pdf/pdfWorker";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { HighlightArea } from "@/components/feature/pdf/types";
import { getHighlightCss } from "@/components/feature/pdf/highlightCss";
import { Loader } from "@/components/ui/loader";

interface PageDimensions {
  width: number;
  height: number;
  intrinsicRotation: number;
}

interface PendingJump {
  pageIndex: number;
  topPercent: number;
  topMargin: number;
}

export interface PdfDocumentViewerHandle {
  jumpToPage: (pageIndex: number) => void;
  jumpToArea: (area: HighlightArea) => void;
}

interface Props {
  fileUrl: string;
  highlights?: HighlightArea[];
  rotation?: number;
  zoom?: number;
  searchText?: string;
  onDocumentLoad?: (pdf: PDFDocumentProxy) => void;
  onLoadError?: (error: Error) => void;
  onCurrentPageChange?: (pageIndex: number) => void;
}

const PAGE_GAP = 8;
const HORIZONTAL_PADDING = 16;
const JUMP_TOP_MARGIN = 64;

// Standard pdf.js lazy loading: fetch only what the current view needs over
// HTTP Range (the /files/document endpoint returns 206), and let each page load
// when it scrolls into view. disableAutoFetch turns off the greedy whole-file
// download; per pdf.js it only takes effect while streaming stays enabled, so
// disableStream is left false. rangeChunkSize is bumped well above the 64 KB
// default so each page loads in fewer, larger round-trips.
// Must be a stable module constant — react-pdf reloads the document if the
// options object identity changes between renders.
// The *Url options point at pdf.js runtime assets copied into the build by
// vite.config.ts (viteStaticCopy). wasmUrl is load-bearing: without it pdf.js 5
// cannot decode JPEG2000 images (the codec scanners produce) and renders those
// pages blank, with only a console warning.
const PDF_OPTIONS = {
  disableAutoFetch: true,
  disableStream: false,
  rangeChunkSize: 4 * 1024 * 1024,
  wasmUrl: "/pdfjs/wasm/",
  cMapUrl: "/pdfjs/cmaps/",
  standardFontDataUrl: "/pdfjs/standard_fonts/",
  iccUrl: "/pdfjs/iccs/",
};

// Fills its nearest positioned ancestor and centres the loader. Used in the
// <Document> loading slot (wrapper is relative min-h-full) and each <Page>
// loading slot (the page div is already relative with a min-height), so the
// spinner sits in the middle of the display area rather than at the top.
const Spinner = () => (
  <div className="absolute inset-0 flex items-center justify-center">
    <Loader className="h-8 w-8 text-coral" />
  </div>
);

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const escapeHtml = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export const PdfDocumentViewer = React.forwardRef<PdfDocumentViewerHandle, Props>(
  (
    {
      fileUrl,
      highlights = [],
      rotation = 0,
      zoom = 1,
      searchText = "",
      onDocumentLoad,
      onLoadError,
      onCurrentPageChange,
    },
    ref,
  ) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const pageRefs = React.useRef<(HTMLDivElement | null)[]>([]);
    const [pageDimensions, setPageDimensions] = React.useState<PageDimensions[]>([]);
    const [containerWidth, setContainerWidth] = React.useState(0);
    const [visiblePages, setVisiblePages] = React.useState<Set<number>>(new Set());
    const [renderedPages, setRenderedPages] = React.useState<Set<number>>(new Set());
    const currentPageRef = React.useRef(0);
    const onCurrentPageChangeRef = React.useRef(onCurrentPageChange);
    onCurrentPageChangeRef.current = onCurrentPageChange;
    const loadTokenRef = React.useRef(0);
    const pendingJumpRef = React.useRef<PendingJump | null>(null);
    const jumpScrollTopRef = React.useRef(0);

    const normalizedRotation = ((rotation % 360) + 360) % 360;
    const renderWidth = Math.max(containerWidth - 2 * HORIZONTAL_PADDING, 0) * zoom;

    // Drop the previous document's layout as soon as the file changes, so a jump
    // can never be measured against pages that belong to the document we just
    // navigated away from.
    React.useEffect(() => {
      loadTokenRef.current++;
      pendingJumpRef.current = null;
      setPageDimensions([]);
      setVisiblePages(new Set());
      setRenderedPages(new Set());
      currentPageRef.current = 0;
    }, [fileUrl]);

    // Render the first page immediately: fetch only page 1, then seed every
    // page's placeholder with its size (PDFs are almost always uniform). Each
    // page's real size is corrected from <Page onLoadSuccess> when it renders.
    // onDocumentLoad fires only once the placeholders are seeded, in the same
    // batch, so a consumer that jumps to a citation on load finds the new
    // document's pages already laid out.
    const handleLoadSuccess = React.useCallback(
      async (pdf: PDFDocumentProxy) => {
        const token = ++loadTokenRef.current;
        const firstPage = await pdf.getPage(1);
        if (loadTokenRef.current !== token) return;
        const viewport = firstPage.getViewport({ scale: 1 });
        const firstDims: PageDimensions = {
          width: viewport.width,
          height: viewport.height,
          intrinsicRotation: firstPage.rotate,
        };
        setPageDimensions(Array.from({ length: pdf.numPages }, () => firstDims));
        onDocumentLoad?.(pdf);
      },
      [onDocumentLoad],
    );

    const handlePageLoadSuccess = React.useCallback((pageIndex: number, page: PDFPageProxy) => {
      const viewport = page.getViewport({ scale: 1 });
      setPageDimensions(previous => {
        const current = previous[pageIndex];
        if (
          current &&
          current.width === viewport.width &&
          current.height === viewport.height &&
          current.intrinsicRotation === page.rotate
        ) {
          return previous;
        }
        const next = [...previous];
        next[pageIndex] = { width: viewport.width, height: viewport.height, intrinsicRotation: page.rotate };
        return next;
      });
    }, []);

    const markRendered = React.useCallback((pageIndex: number) => {
      setRenderedPages(previous => (previous.has(pageIndex) ? previous : new Set(previous).add(pageIndex)));
    }, []);

    React.useEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      const observer = new ResizeObserver(entries => {
        setContainerWidth(entries[0].contentRect.width);
      });
      observer.observe(container);
      return () => observer.disconnect();
    }, []);

    React.useEffect(() => {
      const container = containerRef.current;
      if (!container || pageDimensions.length === 0) return;
      const observer = new IntersectionObserver(
        entries => {
          setVisiblePages(previous => {
            const next = new Set(previous);
            for (const entry of entries) {
              const pageIndex = Number((entry.target as HTMLElement).dataset.pageIndex);
              if (entry.isIntersecting) {
                next.add(pageIndex);
              } else {
                next.delete(pageIndex);
              }
            }
            return next;
          });
          const exiting = entries
            .filter(entry => !entry.isIntersecting)
            .map(entry => Number((entry.target as HTMLElement).dataset.pageIndex));
          if (exiting.length > 0) {
            setRenderedPages(previous => {
              const next = new Set(previous);
              exiting.forEach(pageIndex => next.delete(pageIndex));
              return next;
            });
          }
        },
        { root: container, rootMargin: "150% 0px" },
      );
      pageRefs.current.forEach(pageElement => {
        if (pageElement) observer.observe(pageElement);
      });
      return () => observer.disconnect();
    }, [pageDimensions.length]);

    React.useEffect(() => {
      const container = containerRef.current;
      if (!container || pageDimensions.length === 0) return;
      let frame = 0;
      const handleScroll = () => {
        // Anything that moves the scroll position other than the jump itself is the
        // user taking over (wheel, keys, scrollbar) — stop re-anchoring to the target.
        if (pendingJumpRef.current && Math.abs(container.scrollTop - jumpScrollTopRef.current) > 1) {
          pendingJumpRef.current = null;
        }
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const anchor = container.scrollTop + container.clientHeight / 3;
          let current = 0;
          for (let index = 0; index < pageRefs.current.length; index++) {
            const pageElement = pageRefs.current[index];
            if (pageElement && pageElement.offsetTop <= anchor) {
              current = index;
            } else {
              break;
            }
          }
          if (current !== currentPageRef.current) {
            currentPageRef.current = current;
            onCurrentPageChangeRef.current?.(current);
          }
        });
      };
      container.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll();
      return () => {
        cancelAnimationFrame(frame);
        container.removeEventListener("scroll", handleScroll);
      };
    }, [pageDimensions.length]);

    const previousZoomRef = React.useRef(zoom);
    React.useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      if (previousZoomRef.current !== zoom) {
        const ratio = container.scrollHeight > 0 ? container.scrollTop / container.scrollHeight : 0;
        previousZoomRef.current = zoom;
        requestAnimationFrame(() => {
          container.scrollTop = ratio * container.scrollHeight;
        });
      }
    }, [zoom]);

    // Only page 1 is measured up front, so every other page's offsetTop is a guess
    // until that page renders and reports its real size. Resolving a jump to a single
    // scrollTop lets those corrections reflow the pages above the target afterwards,
    // sliding the viewport onto a different page — badly so on long documents, where
    // the guess is compounded thousands of times. So keep the target as a page plus an
    // offset into it, and re-resolve it against the live layout on every reflow until
    // the user takes over.
    const applyPendingJump = React.useCallback(() => {
      const container = containerRef.current;
      const jump = pendingJumpRef.current;
      if (!container || !jump) return;
      const pageElement = pageRefs.current[jump.pageIndex];
      if (!pageElement) return;
      const target = pageElement.offsetTop + (jump.topPercent / 100) * pageElement.clientHeight - jump.topMargin;
      const top = Math.max(target, 0);
      if (Math.abs(container.scrollTop - top) > 1) {
        container.scrollTop = top;
      }
      jumpScrollTopRef.current = container.scrollTop;
    }, []);

    React.useLayoutEffect(() => {
      applyPendingJump();
    }, [applyPendingJump, pageDimensions, renderWidth, normalizedRotation]);

    React.useImperativeHandle(
      ref,
      () => ({
        jumpToPage: (pageIndex: number) => {
          pendingJumpRef.current = { pageIndex, topPercent: 0, topMargin: PAGE_GAP };
          applyPendingJump();
        },
        jumpToArea: (area: HighlightArea) => {
          const displayedTop = getHighlightCss(area, normalizedRotation).top;
          const topPercent = typeof displayedTop === "string" ? parseFloat(displayedTop) : 0;
          pendingJumpRef.current = { pageIndex: area.pageIndex, topPercent, topMargin: JUMP_TOP_MARGIN };
          applyPendingJump();
        },
      }),
      [applyPendingJump, normalizedRotation],
    );

    const customTextRenderer = React.useMemo(() => {
      if (!searchText) return undefined;
      const pattern = new RegExp(escapeRegExp(escapeHtml(searchText)), "gi");
      return ({ str }: { str: string }) => escapeHtml(str).replace(pattern, match => `<mark>${match}</mark>`);
    }, [searchText]);

    const pagePlaceholderHeight = (dimensions: PageDimensions) => {
      const swapped = normalizedRotation % 180 !== 0;
      const displayedWidth = swapped ? dimensions.height : dimensions.width;
      const displayedHeight = swapped ? dimensions.width : dimensions.height;
      return renderWidth > 0 && displayedWidth > 0 ? (displayedHeight / displayedWidth) * renderWidth : 0;
    };

    return (
      <div
        ref={containerRef}
        className="h-full min-h-0 flex-1 overflow-auto bg-gray-100"
        style={{ overflowAnchor: "none" }}
      >
        {/* react-pdf 11 suspends by default; keep its own loading/error slots, which this viewer
            and PDFViewer's keep-mounted proxy cache are built around. */}
        <Document
          suspense={false}
          file={fileUrl}
          className="relative min-h-full"
          options={PDF_OPTIONS}
          onLoadSuccess={handleLoadSuccess}
          onLoadError={onLoadError}
          loading={<Spinner />}
          error={<div className="flex h-full items-center justify-center p-8 text-red-500">Failed to load PDF.</div>}
        >
          <div className="flex flex-col items-center" style={{ padding: `${PAGE_GAP}px ${HORIZONTAL_PADDING}px` }}>
            {pageDimensions.map((dimensions, pageIndex) => (
              <div
                key={pageIndex}
                ref={element => {
                  pageRefs.current[pageIndex] = element;
                }}
                data-page-index={pageIndex}
                className="relative bg-white shadow"
                style={{
                  width: renderWidth,
                  minHeight: pagePlaceholderHeight(dimensions),
                  marginBottom: PAGE_GAP,
                }}
              >
                {visiblePages.has(pageIndex) && renderWidth > 0 && (
                  <>
                    {!renderedPages.has(pageIndex) && <Spinner />}
                    <Page
                      pageNumber={pageIndex + 1}
                      width={renderWidth}
                      rotate={(dimensions.intrinsicRotation + normalizedRotation) % 360}
                      customTextRenderer={customTextRenderer}
                      onLoadSuccess={page => handlePageLoadSuccess(pageIndex, page)}
                      onRenderSuccess={() => markRendered(pageIndex)}
                      loading={null}
                    />
                  </>
                )}
                <div className="pointer-events-none absolute inset-0">
                  {highlights
                    .filter(area => area.pageIndex === pageIndex)
                    .map((area, index) => (
                      <div
                        key={index}
                        style={{
                          background: "yellow",
                          opacity: 0.4,
                          mixBlendMode: "multiply",
                          ...getHighlightCss(area, normalizedRotation),
                        }}
                      />
                    ))}
                </div>
              </div>
            ))}
          </div>
        </Document>
      </div>
    );
  },
);

PdfDocumentViewer.displayName = "PdfDocumentViewer";
